import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, ArrowUpRight, BatteryFull, BatteryMedium, BatteryLow, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { DeviceRowsSkeleton } from "../components/PageSkeletons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PendingSyncBadge from "../components/PendingSyncBadge";

const batteryMeta = (b) => {
  if (b == null) return { Icon: BatteryMedium, tone: "text-neutral-400", label: "—" };
  if (b > 50) return { Icon: BatteryFull, tone: "text-green-700", label: `${b}%` };
  if (b > 20) return { Icon: BatteryMedium, tone: "text-amber-600", label: `${b}%` };
  return { Icon: BatteryLow, tone: "text-red-600", label: `${b}%` };
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

export default function Devices() {
  const [employees, setEmployees] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ serial_number: "", app_id: "", department: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    // Background retries land on the server every 60s -- poll so a "Pending sync"
    // badge disappears on its own once one succeeds, no manual refresh needed.
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const assigned = employees?.filter((e) => e.device) || [];
  const unassigned = employees?.filter((e) => !e.device) || [];

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ serial_number: emp.device.serial_number, app_id: emp.device.app_id, department: emp.department });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/employees/${editing.id}/device`, form);
      if (data.metadata_sync?.status === "synced") {
        toast.success("Device updated", { description: "Synced to local server" });
      } else {
        toast.success("Device updated", { description: "Saved — will sync once the local server is back online" });
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="devices-title">Devices</h1>
        <p className="text-sm text-neutral-500 mt-1">EXOPILOT pendants recording via BLE to the paired companion phone app.</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between min-w-[860px]">
          <h2 className="text-sm font-medium">Assigned pendants ({assigned.length})</h2>
          <span className="text-[11px] text-neutral-400 font-mono-data">Battery & last-seen reported by the companion phone app</span>
        </div>
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-200">
              <th className="px-6 py-3 font-medium">Serial number</th>
              <th className="px-4 py-3 font-medium">App ID</th>
              <th className="px-4 py-3 font-medium">Employee</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Battery</th>
              <th className="px-4 py-3 font-medium">Last seen</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {employees === null && <DeviceRowsSkeleton />}
            {assigned.map((emp) => {
              const bat = batteryMeta(emp.device.battery);
              return (
              <tr key={emp.id} data-testid={`device-row-${emp.device.serial_number}`} className="hover:bg-neutral-50 transition-colors">
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-2 font-mono-data text-xs">
                    <Radio className="w-3.5 h-3.5 text-green-600" /> {emp.device.serial_number}
                  </span>
                </td>
                <td className="px-4 py-4 font-mono-data text-xs text-neutral-600">{emp.device.app_id}</td>
                <td className="px-4 py-4">
                  <Link to={`/console/employees/${emp.id}`} className="inline-flex items-center gap-1 hover:underline underline-offset-4">
                    {emp.name} <ArrowUpRight className="w-3 h-3 text-neutral-400" />
                  </Link>
                </td>
                <td className="px-4 py-4 text-neutral-600">
                  <div className="flex items-center gap-2">
                    <span>{emp.department}</span>
                    {emp.metadata_sync?.status === "failed" && <PendingSyncBadge testId={`device-pending-sync-${emp.device.serial_number}`} />}
                  </div>
                </td>
                <td className="px-4 py-4" data-testid={`device-battery-${emp.device.serial_number}`}>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-mono-data ${bat.tone}`}>
                    <bat.Icon className="w-4 h-4" /> {bat.label}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs text-neutral-500 font-mono-data" data-testid={`device-lastseen-${emp.device.serial_number}`}>
                  {timeAgo(emp.device.last_seen)}
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">Active</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <button data-testid={`edit-device-${emp.device.serial_number}`} onClick={() => openEdit(emp)} title="Edit device"
                    className="p-2 rounded-md text-neutral-400 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );})}
            {employees !== null && assigned.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-sm text-neutral-400">No pendants assigned yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {unassigned.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="unassigned-list">
          <h2 className="text-sm font-medium">Awaiting a pendant ({unassigned.length})</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {unassigned.map((emp) => (
              <Link key={emp.id} to="/console/employees"
                className="text-xs border border-neutral-300 rounded-full px-3.5 py-1.5 text-neutral-600 hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors">
                {emp.name} · {emp.department}
              </Link>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-device-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Edit device</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2 min-w-0">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Serial number</label>
              <input data-testid="edit-device-serial-input" required value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
              <p className="text-[11px] text-neutral-400 mt-1">Must be unique across all devices.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">App ID</label>
              <input data-testid="edit-device-appid-input" required value={form.app_id}
                onChange={(e) => setForm({ ...form, app_id: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Department</label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} required>
                <SelectTrigger data-testid="edit-device-department-select" className="mt-1.5">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button data-testid="edit-device-submit-button" type="submit" disabled={busy}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
